// =====================================================================
// LAS LLAVES DE ACCESO DE OTRA PERSONA · DIRECTRIZ-LLAVE L-3 (R-LL-3)
// =====================================================================
// En la tarjeta de cada persona de Personal, solo para el Admin Supremo.
// Ve sus llaves y puede QUITARLAS con un motivo escrito (queda en la
// bitácora). Nunca crearlas: una llave es la huella o la cara de alguien
// en SU aparato. Por eso aquí no hay, ni habrá, un botón de agregar, y la
// prueba lo comprueba.
//
// Se abre a petición: la lista de Personal pinta una tarjeta por persona
// y pedir las llaves de todas al cargar serían decenas de consultas que
// casi nadie mira.
import { useCallback, useState } from 'react';
import { ChevronDown, KeyRound, Loader2, Trash2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { fechaDeLlave, faltaDelMotivo } from './llavesDeLaCuenta';

function MotivoDeQuitar({ valor, onCambio }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-gray-700">Motivo (obligatorio)</span>
      <textarea value={valor} onChange={(e) => onCambio(e.target.value)} rows={2} autoFocus maxLength={300}
                id="motivo-quitar-llave" placeholder="Por ejemplo: perdió su teléfono"
                className="entrada" />
      <Ayuda id="seguridad.supremo.llaves.motivo" />
    </label>
  );
}

export default function LlavesDeUnaPersona({ persona }) {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [quitar, setQuitar] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [hecho, setHecho] = useState(null);

  const cargar = useCallback(() => {
    setError(null);
    return api.get(`/supremo/usuarios/${persona.id}/llaves`).then(setDatos)
      .catch((e) => setError(`No se pudieron leer sus llaves de acceso. ${e.message || ''} Pulsa «Volver a intentar».`.replace(/\s+/g, ' ')));
  }, [persona.id]);

  function alternar() {
    const nuevo = !abierto;
    setAbierto(nuevo);
    if (nuevo && !datos) cargar();
  }

  const idPanel = `llaves-de-${persona.id}`;
  const llaves = datos?.llaves || [];
  const nombre = persona.nombre_completo || 'Esta persona';

  return (
    <div className="rounded-lg border border-gray-200" data-bloque="llaves-de-otra-persona">
      <button type="button" onClick={alternar} aria-expanded={abierto} aria-controls={idPanel}
              className="flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50">
        <span className="inline-flex items-center gap-2"><KeyRound className="h-4 w-4" aria-hidden="true" /> Llaves de acceso de esta persona</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${abierto ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {abierto && (
        <div id={idPanel} className="space-y-2 border-t border-gray-100 p-3">
          {!datos && !error && (
            <p role="status" className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando sus llaves…
            </p>
          )}
          {error && (
            <div className="space-y-2">
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>
              <button type="button" onClick={cargar}
                      className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold">Volver a intentar</button>
            </div>
          )}
          {datos && llaves.length === 0 && (
            <p className="text-sm text-gray-600" data-estado="vacio">Esta persona no tiene llaves de acceso.</p>
          )}
          {llaves.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {llaves.map((l) => (
                <li key={l.id} data-llave={l.id} className="space-y-1 py-2">
                  <p className="break-words text-sm font-semibold text-gray-900">{l.apodo || 'Llave sin nombre'}</p>
                  <p className="text-xs text-gray-600">
                    {l.aparato || 'Sin dato del aparato'} · Agregada {fechaDeLlave(l.creado_en) || '—'} · Último uso: {fechaDeLlave(l.ultimo_uso_en) || 'Nunca usada'}
                  </p>
                  {l.bloqueada && (
                    <p className="text-xs font-medium text-red-700">Bloqueada{l.motivo_bloqueo ? `: ${l.motivo_bloqueo}` : ''}. Ya no abre la cuenta.</p>
                  )}
                  <button type="button" onClick={() => { setMotivo(''); setHecho(null); setQuitar(l); }} data-accion="quitar-llave-de-otro"
                          className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" aria-hidden="true" /> Quitar
                  </button>
                </li>
              ))}
            </ul>
          )}
          {hecho && <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-2.5 text-sm text-green-800">{hecho}</p>}
        </div>
      )}
      {quitar && (
        <ModalConfirmar
          titulo={`Quitar la llave «${quitar.apodo || 'sin nombre'}»`}
          descripcion={`${nombre} ya no podrá entrar con esta llave desde ${quitar.aparato || 'ese aparato'}. Sigue pudiendo entrar con su contraseña y su código.`}
          extra={<MotivoDeQuitar valor={motivo} onCambio={setMotivo} />}
          bloqueo={faltaDelMotivo(motivo)}
          peligro
          etiquetaConfirmar="Quitar la llave"
          onConfirmar={async () => {
            const r = await api.post(`/supremo/usuarios/${persona.id}/llaves/${quitar.id}/quitar`, { motivo: motivo.trim() });
            setHecho(r?.mensaje || 'Llave retirada.');
            await cargar();
          }}
          onCerrar={() => setQuitar(null)}
        />
      )}
    </div>
  );
}
